import { ExpandMoreRounded } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from '@mui/material'
import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'

const accordionStyle = {
  '&.MuiAccordionSummary-root': {
    borderRadius: '5px 5px 0px 0px',
    borderBottom: '0.5px solid rgba(255, 255, 255, 0.12)',
    boxShadow: theme => '0px 0px 1px 0px ' + theme.palette.primary.main + '60, 0px 0px 3px 0px ' + theme.palette.primary.main + '20'
  },
  '&.MuiAccordionDetails-root': {
    borderRadius: '0px 0px 5px 5px',
    boxShadow: theme => '0px 2px 1px -1px ' + theme.palette.primary.main + '60, 0px 1px 1px 0px ' + theme.palette.primary.main + '60, 0px 1px 3px 0px ' + theme.palette.primary.main + '20'
  }
}

const DefaultAccordion = (props) => {
  const {
    title = null,
    titleStyle = {},
    children,
    defaultExpanded = true,
    headerRender = null,
    headerSx = null,
    contentSx = null,
    disabled = false,
    onExpandedStatusChange,
    disableUnmount = false,
    // The inner margin of the body. Every screen keeps 2em; the graph of the workflow editor uses less (#167).
    contentPadding = '2em',
    // A number that, each time it changes, opens the accordion and brings it into view: another part of the screen
    // asking to show what this accordion holds (#167: the double click on a row of the graph's panel).
    openSignal = 0,
    // Spread onto the AccordionSummary element itself (listeners, ARIA attributes, a ref): what a
    // sortable list needs to make the header the drag handle. Our own props below always win.
    summaryProps = {}
  } = props

  // Initialize expanded based on defaultExpanded prop
  const [expanded, setExpanded] = useState(defaultExpanded)
  const theme = useTheme()
  const rootRef = useRef(null)
  const lastSignalRef = useRef(openSignal)
  const scrollWhenOpenRef = useRef(false)
  const bringIntoView = () => rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })

  // Only a NEW signal opens it: enabling the accordion later must not open it by itself. Closed, it is brought into
  // view once it has finished opening — measured before, it is only its header, and the data stay out of sight.
  useEffect(() => {
    if (openSignal === lastSignalRef.current) {
      return
    }
    lastSignalRef.current = openSignal
    if (disabled) {
      return
    }
    if (expanded) {
      bringIntoView()
      return
    }
    scrollWhenOpenRef.current = true
    setExpanded(true)
  }, [openSignal, disabled, expanded])

  const onOpened = () => {
    if (scrollWhenOpenRef.current) {
      scrollWhenOpenRef.current = false
      bringIntoView()
    }
  }

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded)
  }

  useEffect(() => {
    if (expanded !== undefined && onExpandedStatusChange instanceof Function) {
      onExpandedStatusChange(expanded)
    }
  }, [expanded, onExpandedStatusChange])

  // If the accordion is disabled, defaultExpanded prop is ignored
  useEffect(() => {
    if (disabled) {
      setExpanded(false)
    }
  }, [disabled])

  const renderTitle = () => {
    if (title === null) {
      return
    }
    return (
      <Typography
        variant='h6'
        component='div'
        sx={titleStyle}
      >
        {title.toString().toLocaleUpperCase()}
      </Typography>
    )
  }

  return (
    <Accordion
      ref={rootRef}
      expanded={expanded}
      TransitionProps={{ unmountOnExit: !disableUnmount, onEntered: onOpened }}
      onChange={handleAccordionChange}
      disabled={disabled}
      disableGutters
    >
      <AccordionSummary
        {...summaryProps}
        sx={{ ...accordionStyle, ...headerSx }}
        expandIcon={
          <ExpandMoreRounded
            sx={accordionStyle}
          />
        }
        aria-controls='panel1a-content'
        id='panel1a-header'
        style={{
          color: expanded ? theme.palette.primary.main : 'inherit'
        }}
        disabled={disabled}
      >
        <Stack justifyContent='space-between' direction='row' alignItems='center' sx={{ width: '100%', paddingRight: '1.5rem' }}>
          {renderTitle()}
          {headerRender}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{ ...accordionStyle, ...contentSx, padding: contentPadding }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  )
}

export default DefaultAccordion

DefaultAccordion.propTypes = {
  /**
   * Title to show on the accordion header
   */
  title: PropTypes.string,
  /**
   * Children to show inside the accordion
   */
  children: PropTypes.node.isRequired,
  /**
   * Set the accordion expanded or not at initial render. True by default
   */
  defaultExpanded: PropTypes.bool,
  /**
   * This will render in the accordion header
   */
  headerRender: PropTypes.node,
  /**
   * Inner margin of the body, '2em' by default
   */
  contentPadding: PropTypes.string,
  /**
   * Each new value opens the accordion and scrolls it into view
   */
  openSignal: PropTypes.number,
  /**
   * Disable the accordion
   */
  disabled: PropTypes.bool,
  /**
   * Props spread onto the header (AccordionSummary) element, e.g. a sortable's drag-handle props
   */
  summaryProps: PropTypes.object
}
